/**
 * /de/video-transkribieren — Video-Transkription reframe bridge (DE).
 *
 * Targets video transkribieren (2k) / video transkribieren kostenlos (900) /
 * youtube video transkribieren. Links to /de/audio-transkribieren pillar.
 */
import { MarketingShell } from './MarketingShell';

export function DeVideoTranskribieren() {
  return (
    <MarketingShell
      currentLocale="de"
      hreflangPath="/video-transkribieren"
      hreflangLocales={['de']}
      documentTitle="Video transkribieren: kostenlos in Text umwandeln · Draftlize"
      documentDescription="Videos kostenlos in Text transkribieren (inkl. YouTube) — und die Entscheidungen aus Meeting-Aufnahmen sichern, bevor sie im Transkript verschwinden."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Transkription</span>
          <h1 className="serif" style={{ fontSize: 'clamp(36px, 4.8vw, 58px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            Video transkribieren –<br />
            <em>und das Wesentliche behalten.</em>
          </h1>
          <p className="lede">
            Meeting-Aufnahme oder YouTube rein, und in Minuten hast du ein Transkript. Praktisch. Aber der Text aus einem Video ist – wie beim Audio – <em>Rohmaterial</em>. Gerade bei Meeting-Videos brauchst du nicht jede Aussage, sondern die <em>Entscheidung</em>, die getroffen wurde, und warum. Hier: wie du Videos kostenlos transkribierst – und der Schritt danach, bei dem Entscheidungen statt Text übrig bleiben.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Video kostenlos transkribieren.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>Video wählen</strong> – Meeting-Aufnahme, Screen-Recording oder YouTube-URL.</li>
            <li><strong>Transkribieren</strong> – ein KI-Tool wandelt die Tonspur in Text um, meist mit Gratis-Kontingent.</li>
            <li><strong>Korrigieren</strong> – Namen und Begriffe prüfen, dann exportieren.</li>
          </ol>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            YouTube-Videos gehen genauso. Untertitel sind „Text zum Lesen“, ein Transkript ist „Text zum Arbeiten“. Und bei Meeting-Videos kommt noch ein Schritt – siehe auch den Ansatz bei <a href="/de/audio-transkribieren" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>Audio transkribieren</a>.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Untertitel vs. Entscheidung</span>
            <h2 className="serif">Nicht beim Text stehen bleiben – <em>die Entscheidung sichern.</em></h2>
          </div>
          <p>Dein Transkriptionstool und Draftlize machen verschiedene Jobs. Das eine macht aus Video Text; das andere hält die Entscheidungen darin aktuell.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>Video-Transkription</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Ergebnis</td><td className="partial">Untertitel / Volltext</td><td className="ours yes">Entscheidungskarten mit Begründung</td></tr>
            <tr><td>Wert des Meeting-Videos</td><td className="no">in jeder Aussage vergraben</td><td className="ours yes">auf wenige Entscheidungen verdichtet</td></tr>
            <tr><td>Drei Wochen später</td><td className="no">zu lang, keiner schaut rein</td><td className="ours yes">per ID auffindbar, aktuell oder markiert</td></tr>
            <tr><td>KI-Agent</td><td className="partial">liest nur Untertitel</td><td className="ours yes">liest Entscheidungen via MCP</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Selbst wenn du ein einstündiges Meeting-Video komplett transkribierst – welche Entscheidung darin noch gilt, sagt dir der Text nicht.
        </blockquote>
        <cite>Video-Transkription macht das Tool. Die Entscheidungen hält Draftlize lebendig.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Häufige Fragen.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Welches Tool für Video-Transkription?', a: 'Ein KI-Transkriptionstool mit Video-Unterstützung wandelt die Tonspur automatisch in Text. Draftlize transkribiert nicht – es macht aus den Entscheidungen im Text aktuelle Karten.' },
            { q: 'Wie transkribiere ich ein YouTube-Video?', a: 'Gib die URL an ein passendes Tool oder exportiere die Auto-Untertitel. Bei Meeting- oder Interview-Videos lohnt der Schritt danach: die Entscheidungen sichern.' },
            { q: 'Funktioniert das mehrsprachig?', a: 'Die meisten Tools unterstützen mehrere Sprachen. Wichtiger als die Sprache ist, welche Entscheidungen nach dem Transkribieren bleiben – dafür ist Draftlize da.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Behalte die Entscheidungen aus jedem Meeting-Video <em>– gratis mit $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>$5 Gratis-Guthaben für neue Accounts</span>
          <span>Zahle nur, was du nutzt</span>
          <span>Guthaben verfällt nie</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Lass die Video-Transkription beim Tool und füge die Zusammenfassung in Draftlize ein. Die Entscheidungen aus dem Meeting werden verknüpfte, aktuelle Karten – auch für deinen KI-Agenten lesbar.
          </p>
          <a className="btn-primary" href="/">Gratis mit $5 starten</a>
        </div>
      </section>
    </MarketingShell>
  );
}
